/**
 * Local-time date helpers for the hub.
 *
 * The shared helpers in src/lib/api.ts (`currentPeriod`, `weekStart`) format via
 * toISOString() — i.e. UTC — so after ~8pm Eastern they report tomorrow's date.
 * The hub says "tonight's dinner" and "Good evening", so it has to think in the
 * family's local time. Everything here is local; nothing here is configurable.
 */

/** YYYY-MM-DD in local time. */
export function isoDate(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** YYYY-MM in local time. */
export function localPeriod(d: Date = new Date()): string {
  return isoDate(d).slice(0, 7);
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

/** Monday of the week containing `d`, local. */
export function localWeekStart(d: Date = new Date()): string {
  const offset = (d.getDay() + 6) % 7; // Mon = 0
  return isoDate(addDays(d, -offset));
}

/** How far through the month we are, 0..1 — the pace marker on the spend bar. */
export function monthElapsed(d: Date = new Date()): number {
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return d.getDate() / daysInMonth;
}

export function greeting(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

/** "Sunday, July 12" */
export function longDate(d: Date = new Date()): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/**
 * Parses the timestamp shapes this app actually stores:
 *   - "YYYY-MM-DD"            → a local calendar day (all-day events, plan dates)
 *   - "YYYY-MM-DD HH:MM:SS"   → D1's datetime('now'), which is UTC with no marker
 *   - full ISO-8601 with zone → used as-is
 */
export function parseWhen(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const s = value.replace(' ', 'T');
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  return new Date(hasZone ? s : `${s}Z`);
}

/** "6:30 PM" */
export function timeOfDay(value: string): string {
  return parseWhen(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Whole local calendar days from today to `value` (negative = past). */
export function daysFromToday(value: string): number {
  const target = parseWhen(value);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** "Today · 6:30 PM" / "Tomorrow" / "Fri, Jul 17 · 9:00 AM" */
export function whenLabel(value: string, allDay: boolean): string {
  const diff = daysFromToday(value);
  const day =
    diff === 0
      ? 'Today'
      : diff === 1
        ? 'Tomorrow'
        : parseWhen(value).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
  return allDay ? day : `${day} · ${timeOfDay(value)}`;
}

/** "just now" / "12m ago" / "3h ago" / "2d ago" */
export function relativeTime(value: string): string {
  const ms = Date.now() - parseWhen(value).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
